import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Animal } from "../data/animals";

interface Props {
  animal: Animal;
  /** Index of the step currently being taught. */
  stepIndex: number;
  /** Seconds to draw ONE stroke of the current step. A step with several
   *  strokes draws them one after another, so it takes proportionally longer. */
  duration: number;
  /** When true, the current step's strokes are shown solid/finished instead of
   *  looping — used during the end-of-drawing celebration. */
  frozen?: boolean;
  /** When true, freeze the current step's draw where it is (pause button). */
  paused?: boolean;
  /** "Still picture" mode: show each step as its finished still image instead of
   *  drawing itself. Same no-motion rendering as prefers-reduced-motion, but the
   *  current step stays highlighted (past steps faded) so it's clear what's new. */
  still?: boolean;
  /** Show the faint alignment grid behind the outline steps (default true).
   *  Turned off for the clean brand look in export/recording mode. */
  showGrid?: boolean;
  /** Reports the current step's live drawing progress (0..1) every animation
   *  frame, so a caller (the play-button ring) can sweep as the pen draws. Sends
   *  0 when nothing is animating. */
  onProgress?: (progress: number) => void;
}

const PENCIL = "#bdb6a8";

/** Near-black ink? Those strokes draw as light chalk on the dark board until
 *  the colouring step gives them fills to sit on (see .ink-stroke in CSS). */
function isDarkInk(color: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (!m) return false;
  const v = parseInt(m[1], 16);
  const r = v >> 16, g = (v >> 8) & 255, b = v & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 90;
}

// When a single step loops for study, how long the fully-drawn step holds on
// screen before it wipes and redraws itself (ms). Matches the calm between-step
// beat used in hands-free/video mode (WATCH_STEP_PAUSE_MS in DrawingPlayer), so
// a looping step lingers long enough to actually study before it redraws.
const STEP_LOOP_HOLD_MS = 2600;

// Faint background grid spacing, in viewBox units (a 200×200 canvas → 10×10
// cells). It's an alignment aid while the outline is being drawn and fades out
// the moment colouring starts.
const GRID_STEP = 20;

/** Interior grid line positions for a canvas of `size` units (edges excluded —
 *  the border already frames the canvas). */
function gridLines(size: number): number[] {
  const lines: number[] = [];
  for (let v = GRID_STEP; v < size; v += GRID_STEP) lines.push(v);
  return lines;
}

// `duration` is the seconds to draw this many path units, so the pen moves at a
// steady speed: a long outline takes longer than a tiny detail (a constant time
// per stroke made big shapes like a car body whip by even on the slowest speed).
const REF_LEN = 200;
// Keep tiny strokes from being instant and huge ones from dragging forever.
const MIN_FACTOR = 0.4;
const MAX_FACTOR = 2.5;

/**
 * Split a path `d` into its separate pen-down sub-paths so the animator can
 * draw each one on its own (one pencil at a time). A stroke like a stick
 * figure's two arms is authored as "M..L.. M..L.." — a single <path> with two
 * sub-paths that would otherwise animate together. We split on absolute `M`
 * (every drawing uses absolute movetos for new sub-paths; a relative `m` would
 * depend on the previous sub-path's end point, and none are used).
 */
function splitSubpaths(d: string): string[] {
  return d
    .split(/(?=M)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Renders the animal as an SVG. Strokes from earlier steps are shown static and
 * faded; the current step's strokes "draw themselves" — but only ONE at a time,
 * like a single pencil: each stroke draws in the order it's authored, the next
 * starts when the previous finishes, and the whole step loops after a short
 * pause. All color fills are painted behind the outlines so a final "color it
 * in" step tints the picture without covering the line art.
 */
export function AnimatedDrawing({ animal, stepIndex, duration, frozen, paused, still, showGrid = true, onProgress }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const visible = animal.steps
    .map((step, si) => ({ step, si }))
    .filter(({ si }) => si <= stepIndex);

  const current = animal.steps[stepIndex];
  const coloringNow = !frozen && !!current && current.strokes.length === 0;
  const allSolid = frozen || coloringNow;
  // "Still picture" mode suppresses the same motion prefers-reduced-motion does
  // (no stroke sequencing, no fill fades) — but, unlike reduce, it does NOT
  // solidify past steps, so the current step stays highlighted (see pastSolid).
  const reduce = prefersReducedMotion();
  const noMotion = reduce || !!still;
  // Some drawings finish with extra steps after the "color it in" reveal (e.g.
  // writing the name). Once we're past that reveal the picture is complete, so
  // its colours stay shown and earlier steps render solid rather than faded.
  const revealIndex = animal.steps.findIndex((s) => s.strokes.length === 0);
  const afterReveal = revealIndex >= 0 && stepIndex > revealIndex;
  // Earlier (non-current) steps render solid once the picture is "done".
  const pastSolid = frozen || coloringNow || afterReveal || reduce;
  // The current step animates one sub-path at a time, so expand any bundled
  // sub-paths (e.g. two arms in one stroke) into separate segments.
  const currentSegments = current ? current.strokes.flatMap(splitSubpaths) : [];
  // Sequence the current step's segments only when we're actually animating.
  const sequencing = !allSolid && !noMotion && currentSegments.length > 0;
  const strokeCount = currentSegments.length;

  // Which stroke of the current step is drawing now (0..strokeCount). When it
  // reaches strokeCount the whole step is drawn; we hold, then loop back to 0.
  const [seq, setSeq] = useState(0);
  const holdRef = useRef<number | undefined>(undefined);

  // Measure each current segment's real length (via the browser) so we can make
  // its draw time proportional to how far the pen travels.
  const measureRef = useRef<SVGPathElement | null>(null);
  const [segLengths, setSegLengths] = useState<number[]>([]);
  useLayoutEffect(() => {
    const p = measureRef.current;
    if (!p) return;
    const segs = current ? current.strokes.flatMap(splitSubpaths) : [];
    setSegLengths(
      segs.map((d) => {
        p.setAttribute("d", d);
        try {
          const len = p.getTotalLength();
          return Number.isFinite(len) && len > 0 ? len : REF_LEN;
        } catch {
          return REF_LEN;
        }
      }),
    );
    // currentSegments is derived from (animal.id, stepIndex); re-measure on those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animal.id, stepIndex]);

  const segDuration = (ki: number): number => {
    const len = segLengths[ki];
    if (!len) return duration;
    const scaled = duration * (len / REF_LEN);
    return Math.min(
      Math.max(scaled, duration * MIN_FACTOR),
      duration * MAX_FACTOR,
    );
  };

  // Each colour is its own "color it in" step (see expandColorSteps). Within a
  // step the matching regions fill ONE AT A TIME — bottom-to-top, then
  // left-to-right — so e.g. the two cheeks fill one then the other, and the
  // nose + eyes step paints nose, one eye, the other eye. "Paper" (white) fills
  // are NOT coloured — they show as paper-coloured holes — so they're excluded
  // from the sequence.
  const colorFills = coloringNow && current?.fills ? current.fills : [];
  const paintedCount = colorFills.filter((f) => !f.paper).length;
  const animatingFills = paintedCount > 0 && !noMotion;

  // Regions that belong to LATER colour steps are shown as paper holes during
  // the current colour step, so the current colour fills AROUND them (you never
  // colour under a region that will be coloured later — on a white sheet you
  // leave it blank and fill it on its turn). Not-yet-reached regions of the
  // current step do the same via the .fill-seq-pending class.
  const futureFills = coloringNow
    ? animal.steps.flatMap((s, i) =>
        i > stepIndex && s.strokes.length === 0 && s.fills ? s.fills : [],
      )
    : [];
  const [fillOrder, setFillOrder] = useState<number[]>([]);
  useLayoutEffect(() => {
    const p = measureRef.current;
    const fills = coloringNow && current?.fills ? current.fills : [];
    if (!p || fills.length === 0) {
      setFillOrder([]);
      return;
    }
    const geo = fills.map((f) => {
      p.setAttribute("d", f.d);
      try {
        const b = p.getBBox();
        return { bottom: b.y + b.height, cx: b.x + b.width / 2 };
      } catch {
        return { bottom: 0, cx: 0 };
      }
    });
    // Only the real (non-paper) regions get sequenced.
    const order = fills.map((_, i) => i).filter((i) => !fills[i].paper);
    // Lowest region first (bottom-to-top); ties go left-to-right.
    order.sort((a, b) => geo[b].bottom - geo[a].bottom || geo[a].cx - geo[b].cx);
    setFillOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animal.id, stepIndex]);

  // How many regions of the current colour step are painted (0..count); the one
  // at `fillSeq` is fading in. Runs once and holds (the child advances steps).
  const [fillSeq, setFillSeq] = useState(0);
  const fillDuration = Math.min(Math.max(duration * 0.3, 0.4), 1);

  // Reset both sequences the instant the step (or drawing) changes — during
  // render, NOT in an effect. An effect runs after paint, so the new step would
  // briefly paint one frame using the previous step's progress (e.g. showing it
  // already fully coloured) before resetting — that was the colouring flash.
  //
  // The previous key is tracked in STATE, not a ref. A ref mutation survives a
  // render that React later discards or rebases (concurrent lanes), so a
  // ref-guarded reset is one-shot — lose that one shot and the stale sequence
  // commits: rapid back-and-forth stepping showed strokes already drawn on
  // arrival. State-tracked prev is discarded along with the render, so the
  // guard re-fires until the reset actually sticks.
  const stepKey = `${animal.id}#${stepIndex}`;
  const [prevStepKey, setPrevStepKey] = useState(stepKey);
  if (prevStepKey !== stepKey) {
    setPrevStepKey(stepKey);
    if (seq !== 0) setSeq(0);
    if (fillSeq !== 0) setFillSeq(0);
  }

  // Once every stroke is drawn, hold a calm beat then loop back to the first.
  useEffect(() => {
    if (!sequencing || paused) return;
    if (seq >= strokeCount) {
      holdRef.current = window.setTimeout(() => setSeq(0), STEP_LOOP_HOLD_MS);
      return () => window.clearTimeout(holdRef.current);
    }
  }, [seq, sequencing, paused, strokeCount]);

  const revealColors = !animal.colorReveal || coloringNow || frozen || afterReveal;

  // Before the colouring step the dark board has no fills — ink strokes render
  // as chalk (CSS flips only .ink-stroke paths, only in the dark theme).
  const preReveal = revealIndex < 0 || stepIndex < revealIndex;

  // The alignment grid guides the outline-drawing phase. It stays mounted (so it
  // can fade rather than blink) but is hidden once colouring starts, or when the
  // picture is frozen/finished or on a post-reveal step (e.g. writing the name).
  const gridActive = !allSolid && !afterReveal;
  const vb = animal.viewBox.split(/\s+/).map(Number);
  const canvasSize = vb[2] > 0 ? vb[2] : 200;
  const gridPositions = gridLines(canvasSize);

  // Report the current step's live drawing progress (0..1) every frame, so the
  // play-button ring can sweep in step with the pen. We read the REAL DOM
  // animation of the active stroke/fill (`getComputedTiming().progress`), so it
  // follows speed changes and freezes when paused for free. Stroke progress is
  // weighted by each segment's draw TIME (segDuration), not its length: the pen
  // clamps very short strokes UP to 0.4× and very long ones DOWN to 2.5×, so
  // length and time diverge — a step like the horse's "meadow" (one long ground
  // line + eight tiny sun rays) would make a length-weighted ring race then
  // stall. Time-weighting keeps it sweeping at a constant speed.
  useEffect(() => {
    if (!onProgress) return;
    const drawingStrokes = sequencing && strokeCount > 0;
    const paintingFills = animatingFills && paintedCount > 0;
    if (allSolid || noMotion || (!drawingStrokes && !paintingFills)) {
      onProgress(0);
      return;
    }
    let raf = 0;
    const activeFrac = (selector: string): number => {
      const el = svgRef.current?.querySelector(selector);
      const timing = (el as SVGElement | null)
        ?.getAnimations?.()[0]
        ?.effect?.getComputedTiming?.();
      const p = timing?.progress;
      return typeof p === "number" ? p : 0;
    };
    const tick = () => {
      let progress = 0;
      if (drawingStrokes) {
        let total = 0;
        for (let i = 0; i < strokeCount; i++) total += segDuration(i);
        total = total || 1;
        let done = 0;
        for (let i = 0; i < Math.min(seq, strokeCount); i++) {
          done += segDuration(i);
        }
        progress =
          seq >= strokeCount
            ? 1
            : (done + activeFrac(".stroke-seq-active") * segDuration(seq)) / total;
      } else {
        progress = (fillSeq + activeFrac(".fill-seq-active")) / paintedCount;
      }
      onProgress(Math.max(0, Math.min(1, progress)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [
    onProgress,
    sequencing,
    animatingFills,
    allSolid,
    noMotion,
    seq,
    strokeCount,
    fillSeq,
    paintedCount,
    segLengths,
    duration,
  ]);

  return (
    <svg
      ref={svgRef}
      className={`drawing${preReveal ? " pre-reveal" : ""}`}
      viewBox={animal.viewBox}
      role="img"
      aria-label={`How to draw a ${animal.name}, step ${stepIndex + 1}`}
    >
      {/* Invisible helper path used only to measure stroke lengths. */}
      <path ref={measureRef} fill="none" stroke="none" aria-hidden="true" />

      {/* Faint alignment grid, behind everything — an aid while the child copies
          the outline; it fades out once colouring begins. */}
      {showGrid && (
        <g
          className={`draw-grid${gridActive ? "" : " draw-grid-hidden"}`}
          aria-hidden="true"
        >
          {gridPositions.map((v) => (
            <Fragment key={v}>
              <line x1={v} y1={0} x2={v} y2={canvasSize} />
              <line x1={0} y1={v} x2={canvasSize} y2={v} />
            </Fragment>
          ))}
        </g>
      )}

      {/* Pass 1: color fills, behind everything. The current step's regions fade
          in one at a time (see fillOrder); earlier steps' fills stay solid. */}
      {visible.map(({ step, si }) =>
        step.fills?.map((f, fi) => {
          // Paper (white) regions are never coloured — they render as a paper-
          // coloured hole (matches the canvas), always solid, so the colour
          // beneath fills around them.
          if (f.paper) {
            return (
              <path
                key={`fill-${si}-${fi}`}
                d={f.d}
                stroke="none"
                fillRule={f.evenodd ? "evenodd" : undefined}
                className="fill-paper"
              />
            );
          }

          // Frozen (finished/celebration) shows every fill solid with NO fade:
          // using fill-current here re-ran the fade-in on all fills at once, so
          // pressing next on the last colour step flashed every colour away for
          // a moment. fill-done is opacity 1 with no animation.
          let className = frozen
            ? "fill-done"
            : si === stepIndex
              ? "fill-current"
              : "fill-done";
          let style: React.CSSProperties | undefined;
          let onAnimationEnd: (() => void) | undefined;
          let key = `fill-${si}-${fi}`;

          // Sequence the current colour step's regions once they're measured.
          if (
            animatingFills &&
            si === stepIndex &&
            fillOrder.length === paintedCount
          ) {
            const pos = fillOrder.indexOf(fi);
            if (pos < fillSeq) {
              className = "fill-seq-done";
            } else if (pos === fillSeq) {
              className = "fill-seq-active";
              style = {
                "--fill-dur": `${fillDuration}s`,
                animationPlayState: paused ? "paused" : "running",
              } as React.CSSProperties;
              onAnimationEnd = () => setFillSeq((s) => (s === pos ? s + 1 : s));
              // Remount when (re)activated so the fade restarts cleanly.
              key = `fill-${si}-${fi}-a${fillSeq}`;
            } else {
              className = "fill-seq-pending";
            }
          }

          return (
            <path
              key={key}
              d={f.d}
              fill={f.color}
              stroke="none"
              fillRule={f.evenodd ? "evenodd" : undefined}
              className={className}
              style={style}
              onAnimationEnd={onAnimationEnd}
            />
          );
        }),
      )}

      {/* Regions that will be coloured in a later step, shown as paper holes now
          so the current colour fills around them (never under them). */}
      {futureFills.map((f, i) => (
        <path key={`future-${i}`} d={f.d} stroke="none" fillRule={f.evenodd ? "evenodd" : undefined} className="fill-hole" />
      ))}

      {/* Pass 2: outline strokes, on top of the fills. */}
      {visible.map(({ step, si }) => {
        const isCurrent = si === stepIndex;
        const strokeColor = revealColors ? step.color ?? animal.color : PENCIL;
        // While this step is animating we draw its sub-paths one at a time;
        // other steps (and frozen/coloring states) render their strokes whole.
        const paths = isCurrent && sequencing ? currentSegments : step.strokes;

        return paths.map((d, ki) => {
          // Decide how this particular stroke renders.
          let className: string;
          let style: React.CSSProperties | undefined;
          let onAnimationEnd: (() => void) | undefined;

          if (!isCurrent) {
            // Earlier step: solid once the picture is complete (final reveal,
            // celebration, a post-reveal step like writing the name, or reduced
            // motion), otherwise faded to highlight the current step. Solid
            // matters for pictures whose colour comes from strokes (paintings,
            // family, rainbow) — they'd look washed out faded.
            className = pastSolid ? "stroke-final" : "stroke-done";
          } else if (allSolid || noMotion) {
            className = "stroke-final";
          } else if (ki < seq) {
            className = "stroke-seq-drawn"; // already drawn this loop
          } else if (ki === seq) {
            className = "stroke-seq-active"; // the one being drawn now
            style = {
              "--seg": `${segDuration(ki)}s`,
              animationPlayState: paused ? "paused" : "running",
            } as React.CSSProperties;
            onAnimationEnd = () => setSeq((s) => (s === ki ? s + 1 : s));
          } else {
            className = "stroke-seq-pending"; // not drawn yet (invisible)
          }

          return (
            <path
              // Include seq in the active stroke's key so it remounts and the
              // animation restarts cleanly each loop.
              key={`${si}-${ki}-${isCurrent ? `c${ki === seq ? seq : "x"}` : "done"}`}
              d={d}
              pathLength={1}
              fill="none"
              stroke={strokeColor}
              strokeWidth={step.strokeWidth ?? 4}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isDarkInk(strokeColor) ? `${className} ink-stroke` : className}
              style={style}
              onAnimationEnd={onAnimationEnd}
            />
          );
        });
      })}
    </svg>
  );
}
